import { CycleCountSessionItem } from '../models/cycle-count-session.model';
import { parseAdjustedQty } from './cycle-count-adjustment-input.utils';

export interface ResolvedDamage {
  damagedQty: number | null;
  isDamaged: boolean;
}

/**
 * Resolves the raw Broken field into the persisted damage state (#30579).
 * Broken is a quantity (matching the Warehouse Inventory count screen), not a
 * yes/no flag. Empty or 0 means no damage (damagedQty null, isDamaged false); a
 * positive quantity sets both. Returns 'INVALID'/'NEGATIVE' so the caller can
 * warn instead of storing garbage.
 */
export function resolveDamagedQty(
  brokenText: string | number | null | undefined
): ResolvedDamage | 'INVALID' | 'NEGATIVE' {
  const parsed = parseAdjustedQty(brokenText);
  if (parsed === 'INVALID') return 'INVALID';
  if (parsed === 'NEGATIVE') return 'NEGATIVE';
  const qty = parsed === null || parsed === 0 ? null : parsed;
  return { damagedQty: qty, isDamaged: qty != null && qty > 0 };
}

/**
 * #32232: merges a fresh server item list into the local grid for the Sync
 * button when several counters share one session. Keeps the local row ORDER (so
 * the grid does not jump), replaces each row with the server version so other
 * users' counts + `countedByName` show up, PRESERVES any row saved by this user
 * during the Sync fetch (`skip` — see below), drops rows another user deleted,
 * and appends rows another user added.
 *
 * `skip` accepts a single id, an iterable/Set of ids, or null. It MUST cover
 * every row this user saved across the whole lifetime of the Sync GET, not just
 * the one saving at the instant the GET returns: a PUT can succeed (clearing the
 * single "saving" marker) after the GET was issued but before it responds, so
 * the GET's snapshot is stale for that row and would silently overwrite the
 * just-saved count (codex-ryn, PR 3593 thread43057). Callers seed the set with
 * the row saving when Sync starts and add any row that begins saving while the
 * GET is in flight.
 */
export function mergeCycleCountItems(
  local: CycleCountSessionItem[],
  server: CycleCountSessionItem[],
  skip: string | Iterable<string> | null
): CycleCountSessionItem[] {
  const skipIds: ReadonlySet<string> =
    skip == null
      ? new Set<string>()
      : typeof skip === 'string'
        ? new Set<string>([skip])
        : skip instanceof Set
          ? (skip as ReadonlySet<string>)
          : new Set<string>(skip);
  const serverById = new Map(server.map((i) => [i.cycleCountItemId, i]));
  const seen = new Set<string>();
  const merged: CycleCountSessionItem[] = [];
  for (const item of local) {
    const id = item.cycleCountItemId;
    if (skipIds.has(id)) {
      merged.push(item); // in-flight row — keep the local (just-saved) value
      seen.add(id);
      continue;
    }
    const fromServer = serverById.get(id);
    if (fromServer) {
      merged.push(fromServer);
      seen.add(id);
    }
    // else: another user deleted it since load — omit
  }
  // Rows another user added since load — appended in server order. A `skip` id
  // that is NOT in local was deleted locally during the fetch (this user's own
  // in-flight DELETE), so a stale server snapshot must not resurrect it: never
  // append a skip id (grok_code-ryn, PR 3593 delete/sync race).
  for (const item of server) {
    const id = item.cycleCountItemId;
    if (!seen.has(id) && !skipIds.has(id)) merged.push(item);
  }
  return merged;
}

/**
 * #32232 (PR 3593, codex-ryn): tracks the item ids that currently have a write
 * (a count PUT or a delete) in flight, so the Sync guard can be seeded with ALL
 * pending writes — not just the scalar `savingItemId`, which only remembers the
 * LAST save started and so drops earlier concurrent saves (two PUTs in flight →
 * a stale Sync GET overwrites the first). A ref-COUNT (not a bare Set) so
 * overlapping writes to the same id are only cleared when the last one settles.
 */
export class PendingWriteTracker {
  private counts = new Map<string, number>();

  /** Mark a write started for `id`. */
  add(id: string): void {
    this.counts.set(id, (this.counts.get(id) ?? 0) + 1);
  }

  /** Mark a write settled (success or error) for `id`. */
  remove(id: string): void {
    const next = (this.counts.get(id) ?? 0) - 1;
    if (next <= 0) this.counts.delete(id);
    else this.counts.set(id, next);
  }

  /** A snapshot Set of every id with at least one write in flight. */
  ids(): Set<string> {
    return new Set(this.counts.keys());
  }

  get size(): number {
    return this.counts.size;
  }
}

/** Why a material-entry draft could not be persisted (drives the alert copy). */
export type CycleCountEntryError =
  | 'ENTER_COUNT'
  | 'INVALID_COUNT'
  | 'NEGATIVE_COUNT'
  | 'INVALID_BROKEN'
  | 'NEGATIVE_BROKEN';

/** The persisted state resolved from a material-entry draft. */
export interface ResolvedCycleCountEntry {
  actualQty: number | null;
  damagedQty: number | null;
  isDamaged: boolean;
  isNotFound: boolean;
}

/**
 * Outcome of validating+resolving a material-entry draft: either the persisted
 * entry (an object), or a typed error string the caller maps to an alert. A
 * string-or-object result (matching resolveDamagedQty) lets the caller narrow
 * with a `typeof === 'string'` guard, which works in this non-strict tsconfig
 * where boolean-discriminant narrowing does not.
 */
export type CycleCountEntryResult =
  | ResolvedCycleCountEntry
  | CycleCountEntryError;

/**
 * Validates a material-entry draft and resolves it into the persisted count
 * state, or a typed error string when it cannot be saved. This is the single
 * decision behind Save Count, extracted from the counting screen so it is
 * unit-testable.
 *
 * Rules:
 * - Not Found short-circuits: count/broken are ignored and the entry is the
 *   not-found record (mutually exclusive with a count, #30579).
 * - Count and Broken are each validated (INVALID/NEGATIVE surface their own
 *   error so the counter fixes the right field).
 * - #31259: an empty Count is NOT an error when a positive Broken quantity was
 *   entered — that is a real observation (the stock was found and is damaged).
 *   The count is persisted as 0 good-on-hand with the broken quantity recorded,
 *   rather than blocking with "Enter a count". Only a wholly empty entry
 *   (no count, no broken, not Not Found) is rejected with ENTER_COUNT.
 */
export function resolveCycleCountEntry(
  draft: EntryDraft
): CycleCountEntryResult {
  if (draft.notFound) {
    return {
      actualQty: null,
      damagedQty: null,
      isDamaged: false,
      isNotFound: true,
    };
  }

  const count = parseAdjustedQty(draft.qtyText);
  if (count === 'INVALID') return 'INVALID_COUNT';
  if (count === 'NEGATIVE') return 'NEGATIVE_COUNT';

  const damage = resolveDamagedQty(draft.brokenText);
  if (damage === 'INVALID') return 'INVALID_BROKEN';
  if (damage === 'NEGATIVE') return 'NEGATIVE_BROKEN';

  if (count === null) {
    // Count left empty. A recorded Broken quantity still counts (#31259):
    // persist zero good-on-hand with the damage. A blank entry is rejected.
    if (damage.isDamaged) {
      return {
        actualQty: 0,
        damagedQty: damage.damagedQty,
        isDamaged: true,
        isNotFound: false,
      };
    }
    return 'ENTER_COUNT';
  }

  return {
    actualQty: count,
    damagedQty: damage.damagedQty,
    isDamaged: damage.isDamaged,
    isNotFound: false,
  };
}

/**
 * Minimal shape needed to resolve a scanned/typed sub-location to its group.
 * The counting screen's SubLocationGroup satisfies this via `code` + `name`.
 */
export interface ScannableSubLocation {
  code: string;
  name: string;
}

/** The full-page material entry's editable fields, as raw UI values. */
export interface EntryDraft {
  qtyText: string;
  brokenText: string;
  notFound: boolean;
  notes: string;
}

/**
 * True when the material-entry draft differs from the item it was seeded from
 * (#30579). Used by save-on-back: an unchanged entry backs out without saving
 * (so it never trips the "enter a count" validation), while any edit to count,
 * broken, Not Found, or notes commits on back. Count/broken are ignored while
 * Not Found is on (those fields are cleared then).
 */
export function isCycleCountEntryDirty(
  item: CycleCountSessionItem,
  draft: EntryDraft
): boolean {
  const seedQty = item.actualQty != null ? String(item.actualQty) : '';
  const seedBroken = item.damagedQty != null ? String(item.damagedQty) : '';
  const seedNotes = item.notes ?? '';
  if (draft.notFound !== item.isNotFound) return true;
  if ((draft.notes ?? '') !== seedNotes) return true;
  if (draft.notFound) return false;
  return draft.qtyText !== seedQty || draft.brokenText !== seedBroken;
}

/**
 * True when a sub-location code is a countable bin location (#30579).
 *
 * The counting screen only counts materials in dotted XX.YY.ZZ locations. Any
 * code with no dot — one-word areas like CAGE/Yard and customer-name staging
 * areas — is excluded. A dotted code must have at least two non-empty segments,
 * so malformed codes ("A.", ".B", "A..B") are rejected rather than treated as
 * valid bins.
 */
export function isCountableSubLocation(
  code: string | null | undefined
): boolean {
  const trimmed = (code ?? '').trim();
  if (!trimmed.includes('.')) return false;
  const segments = trimmed.split('.');
  return segments.length >= 2 && segments.every((s) => s.trim().length > 0);
}

/**
 * Returns a new array containing only the items in countable (dotted) bin
 * locations. Applied at load time so the whole session — groups, progress, and
 * submit — is scoped to XX.YY.ZZ locations (#30579). Does not mutate the input.
 */
export function filterCountableSubLocationItems(
  items: CycleCountSessionItem[]
): CycleCountSessionItem[] {
  return items.filter((i) => isCountableSubLocation(i.subLocationCode));
}

/**
 * Filters the items shown in a sub-location by a free-text query (#30579).
 * A bin can hold more than one material, so the materials list has a search
 * bar. Matching is trimmed, case-insensitive, and substring against both the
 * material id and the material description. An empty query returns all items.
 * Non-mutating.
 */
export function filterItemsByQuery(
  items: CycleCountSessionItem[],
  query: string | null | undefined
): CycleCountSessionItem[] {
  const needle = (query ?? '').trim().toLowerCase();
  if (needle === '') return items.slice();
  return items.filter(
    (i) =>
      (i.materialId ?? '').toLowerCase().includes(needle) ||
      (i.materialDescription ?? '').toLowerCase().includes(needle)
  );
}

/**
 * Resolves a scanned/typed value to a single material on the items view (#30579),
 * for opening it directly on Enter or barcode scan. An exact material-id match
 * (trimmed, case-insensitive) is authoritative. Otherwise, if the free-text
 * filter narrows to exactly one item, that item is returned. Returns null when
 * the value is empty, matches nothing, or is still ambiguous (so Enter never
 * opens the wrong material).
 */
export function findItemByScan(
  items: CycleCountSessionItem[],
  scanned: string | null | undefined
): CycleCountSessionItem | null {
  const needle = (scanned ?? '').trim().toLowerCase();
  if (needle === '') return null;
  const exact = items.find(
    (i) => (i.materialId ?? '').trim().toLowerCase() === needle
  );
  if (exact) return exact;
  const matches = filterItemsByQuery(items, needle);
  return matches.length === 1 ? matches[0] : null;
}

/**
 * Resolves a scanned or typed sub-location to its group on the locations view
 * (#30579). Matching is trimmed and case-insensitive. A code match is
 * authoritative and takes precedence over a name match, so scanning a real bin
 * code never opens a name-collision group. Returns null when the scan is empty
 * or matches nothing, so the caller can warn instead of opening the wrong bin.
 */
export function findSubLocationGroupByScan<T extends ScannableSubLocation>(
  groups: T[],
  scanned: string | null | undefined
): T | null {
  const needle = (scanned ?? '').trim().toLowerCase();
  if (needle === '') return null;
  const byCode = groups.find(
    (g) => (g.code ?? '').trim().toLowerCase() === needle
  );
  if (byCode) return byCode;
  const byName = groups.find(
    (g) => (g.name ?? '').trim().toLowerCase() === needle
  );
  return byName ?? null;
}
